<script lang="ts">
  import type { FormComponentSlotProps } from "@inertiajs/core"
  import { Form, page } from "@inertiajs/svelte"
  import { fly } from "svelte/transition"

  import DeleteUser from "@/components/delete-user.svelte"
  import HeadingSmall from "@/components/heading-small.svelte"
  import InputError from "@/components/input-error.svelte"
  import { Button } from "@/components/ui/button"
  import { Input } from "@/components/ui/input"
  import { Label } from "@/components/ui/label"
  import AppLayout from "@/layouts/app-layout.svelte"
  import SettingsLayout from "@/layouts/settings/layout.svelte"
  import { settingsProfilePath } from "@/routes"
  import { type BreadcrumbItem } from "@/types"
  import {
    getCurrentSubscription,
    removeSubscriptionFromServer,
    requestPushPermission,
    saveSubscriptionToServer,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
  } from "@/utils/push-notifications"
  import { getServiceWorkerStatus } from "@/utils/service-worker"

  let pushEnabled = $state(false)
  let pushLoading = $state(false)
  let pushError = $state<string | null>(null)
  let serviceWorkerStatus = $state<{
    registered: boolean
    active: boolean
    error?: string
  } | null>(null)

  $effect(() => {
    checkPushSubscription()
    checkServiceWorkerStatus()
  })

  async function checkPushSubscription() {
    const subscription = await getCurrentSubscription()
    pushEnabled = subscription !== null
  }

  async function checkServiceWorkerStatus() {
    const status = await getServiceWorkerStatus()
    serviceWorkerStatus = status
    if (status.error) {
      pushError = status.error
    }
  }

  async function enablePushNotifications() {
    pushLoading = true
    pushError = null

    try {
      if (!("serviceWorker" in navigator)) {
        pushError = "Service workers are not supported in this browser"
        pushLoading = false
        return
      }

      if (!("PushManager" in window)) {
        pushError = "Push notifications are not supported in this browser"
        pushLoading = false
        return
      }

      const status = await getServiceWorkerStatus()
      if (!status.active) {
        pushError =
          status.error ||
          "Service worker is not active. Please refresh the page and try again."
        pushLoading = false
        return
      }

      const hasPermission = await requestPushPermission()
      if (!hasPermission) {
        if (Notification.permission === "denied") {
          pushError =
            "Notification permission was denied. Please enable it in your browser settings."
        } else {
          pushError = "Notification permission denied"
        }
        pushLoading = false
        return
      }

      let subscription: PushSubscription | null = null
      try {
        subscription = await subscribeToPushNotifications()
      } catch (error) {
        pushError =
          error instanceof Error
            ? error.message
            : "Failed to subscribe to push notifications"
        pushLoading = false
        return
      }

      if (!subscription) {
        pushError = "Failed to subscribe to push notifications"
        pushLoading = false
        return
      }

      const saved = await saveSubscriptionToServer(subscription)
      if (!saved) {
        pushError = "Failed to save subscription"
        await unsubscribeFromPushNotifications()
        pushLoading = false
        return
      }

      pushEnabled = true
    } catch (error) {
      console.error("Error enabling push notifications:", error)
      pushError = "An error occurred while enabling push notifications"
    } finally {
      pushLoading = false
    }
  }

  async function disablePushNotifications() {
    pushLoading = true
    pushError = null

    try {
      const subscription = await getCurrentSubscription()
      if (subscription) {
        await removeSubscriptionFromServer(subscription.endpoint)
        await unsubscribeFromPushNotifications()
      }

      pushEnabled = false
    } catch (error) {
      console.error("Error disabling push notifications:", error)
      pushError = "An error occurred while disabling push notifications"
    } finally {
      pushLoading = false
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Profile settings",
      href: settingsProfilePath(),
    },
  ]
</script>

<svelte:head>
  <title>{breadcrumbs[breadcrumbs.length - 1].title} - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <SettingsLayout>
    <div class="flex flex-col space-y-6">
      <HeadingSmall
        title="Profile information"
        description="Update your name and email address"
      />

      <Form
        method="patch"
        action={settingsProfilePath()}
        options={{
          preserveScroll: true,
        }}
        class="space-y-6"
      >
        {#snippet children({
          errors,
          processing,
          recentlySuccessful,
        }: FormComponentSlotProps)}
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input
              id="name"
              name="name"
              class="mt-1 block w-full"
              value={$page.props.auth.user?.name || ""}
              required
              autocomplete="name"
              placeholder="Full name"
            />
            <InputError class="mt-2" message={errors.name} />
          </div>

          <div class="flex items-center gap-4">
            <Button type="submit" disabled={processing}>Save</Button>

            {#if recentlySuccessful}
              <p
                class="text-sm text-neutral-600"
                in:fly={{ y: -10, duration: 200 }}
                out:fly={{ y: -10, duration: 200 }}
              >
                Saved.
              </p>
            {/if}
          </div>
        {/snippet}
      </Form>
    </div>

    <div class="flex flex-col space-y-6">
      <HeadingSmall
        title="Push notifications"
        description="Receive notifications when tasks are assigned, completed, or approved"
      />

      <div class="space-y-4">
        {#if pushError}
          <div
            class="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400"
          >
            {pushError}
          </div>
        {/if}

        <div class="flex items-center justify-between">
          <div class="space-y-0.5">
            <Label>Enable push notifications</Label>
            <p class="text-muted-foreground text-sm">
              Get notified when tasks are updated, even when the app is closed
            </p>
          </div>
          <Button
            type="button"
            variant={pushEnabled ? "destructive" : "default"}
            disabled={pushLoading ||
              (serviceWorkerStatus !== null &&
                !serviceWorkerStatus.active &&
                !pushEnabled)}
            onclick={pushEnabled
              ? disablePushNotifications
              : enablePushNotifications}
          >
            {pushLoading ? "Loading..." : pushEnabled ? "Disable" : "Enable"}
          </Button>
        </div>
      </div>
    </div>

    <DeleteUser />
  </SettingsLayout>
</AppLayout>
